import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import { playOnHover } from "../utils/stickers";
/**
 * The chat's own sticker picker.
 *
 * ramon/stickers ships one, and reusing it was the first attempt — but it is
 * `require`d lazily inside that extension's own open handler, so it registers
 * itself in Flarum's export registry only *after* somebody has opened it from a
 * discussion composer. Until then the lookup correctly reports it as absent, and
 * the chat's button never appeared. Nothing here can force that module to load.
 *
 * So this reads the same data through the API instead: `stickers` is a JSON:API
 * resource, and the extension registers its model in the store at boot, which is
 * a signal that is true from the first render.
 *
 * Inserting is a shortcode, not markup: the formatter turns `:name:` into the
 * sticker on send, exactly as it does for a message typed by hand.
 */
export default class StickerPicker extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "stickers", []);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "filter", "");
    _defineProperty(this, "outsideListener", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  oncreate(vnode) {
    var _vnode$dom$querySelec;
    super.oncreate(vnode);

    // Deferred by a frame: the click that opened the picker is still propagating,
    // and binding synchronously would close it again immediately.
    requestAnimationFrame(() => {
      this.outsideListener = e => {
        if (!vnode.dom.contains(e.target)) this.attrs.onClose();
      };
      document.addEventListener("click", this.outsideListener);
    });
    (_vnode$dom$querySelec = vnode.dom.querySelector(".ChatStickerPicker-search")) == null || _vnode$dom$querySelec.focus();
    playOnHover(vnode.dom);
  }

  /**
   * Hydrate after every render, not only on create: the grid is rebuilt whenever
   * the filter changes, and the new nodes arrive unhydrated.
   */
  onupdate(vnode) {
    super.onupdate(vnode);
    playOnHover(vnode.dom);
  }
  onremove() {
    if (this.outsideListener) document.removeEventListener("click", this.outsideListener);
  }
  view() {
    const term = this.filter.trim().toLowerCase();
    const shown = term ? this.stickers.filter(sticker => {
      var _sticker$title, _sticker$textToReplac, _sticker$categoryName;
      return "".concat((_sticker$title = sticker.title()) != null ? _sticker$title : "", " ").concat((_sticker$textToReplac = sticker.textToReplace()) != null ? _sticker$textToReplac : "", " ").concat((_sticker$categoryName = sticker.categoryName()) != null ? _sticker$categoryName : "").toLowerCase().includes(term);
    }) : this.stickers;
    return m("div", {
      className: "ChatStickerPicker",
      onkeydown: e => this.onKey(e)
    }, m("input", {
      className: "FormControl ChatStickerPicker-search",
      type: "search",
      placeholder: app.translator.trans("ramon-chat.forum.composer.sticker_search", {}, true),
      value: this.filter,
      oninput: e => {
        this.filter = e.target.value;
      }
    }), this.loading ? m("div", {
      className: "ChatStickerPicker-loading"
    }, m(LoadingIndicator, {
      display: "inline",
      size: "small"
    })) : shown.length === 0 ? m("div", {
      className: "ChatStickerPicker-empty"
    }, app.translator.trans("ramon-chat.forum.composer.sticker_none")) : m("div", {
      className: "ChatStickerPicker-grid"
    }, shown.map(sticker => {
      var _sticker$title2;
      return m("button", {
        type: "button",
        key: sticker.id(),
        className: "ChatStickerPicker-item",
        title: (_sticker$title2 = sticker.title()) != null ? _sticker$title2 : sticker.textToReplace(),
        onclick: () => {
          var _sticker$textToReplac2;
          return this.attrs.onInsert((_sticker$textToReplac2 = sticker.textToReplace()) != null ? _sticker$textToReplac2 : "");
        }
      }, this.thumbnail(sticker));
    })));
  }

  /**
   * The thumbnail, in the markup ramon/stickers itself emits.
   *
   * A `.tgs` is gzip-compressed Lottie and a `.json` is Lottie; neither renders in
   * an `<img>`, and the first version of this drew their names instead — which on
   * a library of animated stickers meant a grid of words.
   *
   * Rather than reimplementing those renderers, this emits the exact markup the
   * extension's formatter produces, and its MutationObserver on `document.body`
   * picks them up and animates them. That observer already exists to hydrate
   * stickers inside posts; it does not care where the nodes came from.
   *
   * The path is resolved the same way too: a relative one is served from the forum
   * root, which is what their PHP does before writing the attribute.
   */
  thumbnail(sticker) {
    var _sticker$path, _sticker$title3;
    const path = this.absolute(String((_sticker$path = sticker.path()) != null ? _sticker$path : ""));
    const title = (_sticker$title3 = sticker.title()) != null ? _sticker$title3 : "";
    const lower = path.toLowerCase();
    if (lower.endsWith(".tgs")) {
      return m("span", {
        className: "Sticker Sticker--tgs",
        "data-tgs": path,
        title: title
      });
    }
    if (lower.endsWith(".json")) {
      return m("span", {
        className: "Sticker Sticker--lottie",
        "data-lottie": path,
        title: title
      });
    }
    return m("span", {
      className: "Sticker"
    }, m("img", {
      className: "sticker",
      src: path,
      alt: title,
      loading: "lazy"
    }));
  }
  absolute(path) {
    var _app$forum$attribute;
    if (/^https?:\/\//i.test(path)) return path;
    const base = String((_app$forum$attribute = app.forum.attribute("baseUrl")) != null ? _app$forum$attribute : "").replace(/\/$/, "");
    return base + (path.startsWith("/") ? path : "/".concat(path));
  }
  onKey(e) {
    if (e.key === "Escape") {
      e.stopPropagation();
      this.attrs.onClose();
    }
  }
  async load() {
    try {
      const results = await app.store.find("stickers", {
        page: {
          limit: 200
        }
      });
      this.stickers = Array.isArray(results) ? results : [];
    } catch (_unused) {
      this.stickers = [];
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ramon-chat', 'forum/components/StickerPicker', StickerPicker);